import { useCallback, useMemo, useRef, useState } from 'react';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { isUrl } from 'utils/url';
import { DropEvent, DropzoneOptions, FileRejection } from 'react-dropzone';
import { UploadAreaContext } from './context';

interface UploadAreaProviderProps {
  children: React.ReactNode;
  onTextSubmit: (raw: string) => void;
  onUrlPaste: (url: string) => void;
  onDrop: <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[], event: DropEvent) => void;
  mediaType: CF.UI.UISupportedUploadTypes;
  dropzoneOptions?: DropzoneOptions;
}

export const UploadAreaProvider = (props: UploadAreaProviderProps): JSX.Element => {
  const { children, onTextSubmit, onUrlPaste, onDrop, mediaType, dropzoneOptions } = props;

  const [textValue, setTextValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const onTextChange = useCallback((x) => setTextValue(x.target.value), []);

  const submitHandler = useCallback(
    (e: React.FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      if (isUrl(textValue)) {
        onUrlPaste(textValue);
      } else if (mediaType === 'text') {
        onTextSubmit(textValue);
      } else {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(0, textValue.length);
        cogoToast.error('Please paste valid file URL.');
      }
    },
    [isUrl, textValue, onTextSubmit, inputRef],
  );

  const values = useMemo(
    () => ({
      submitHandler,
      onUrlPaste,
      onDrop,
      mediaType,
      dropzoneOptions,
      textValue,
      onTextChange,
      inputRef,
    }),
    [submitHandler, onUrlPaste, onDrop, mediaType, dropzoneOptions, textValue, onTextChange, inputRef],
  );

  return <UploadAreaContext.Provider value={values}>{children}</UploadAreaContext.Provider>;
};
